import React from 'react';
import brain1 from './img/brain-1.svg';
import brain2 from './img/brain-2.svg';
import brain3 from './img/brain-3.svg';
import brain4 from './img/brain-4.svg';
import brain5 from './img/brain-5.svg';
import { useState, useEffect } from 'react';
import { useClient } from 'jsonapi-react';
import { FiBarChart2 } from 'react-icons/fi';
import FlashcardGuideModal from '@/pages/StudyPlan/StudyBlock/FlashcardGuideModal';
import { RetentionMetricsDialog } from './retention-metrics-dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import usePlayfulStudent from '@/hooks/usePlayfulStudent';
import { PlayfulCard, PlayfulCardButton } from '@/components/custom/playful-card';
import './study-guide-review.sass';

// Ramp corporativo (semáforo genérico) vs. ramp playful (segue os tokens de
// src/styles/themes/theme.playful.sass) — o corporativo fica intocado.
const CORPORATE_PROGRESS_COLORS = { 1: '#EF4444', 2: '#F97316', 3: '#EAB308', 4: '#22C55E', 5: '#10B981' };
const PLAYFUL_PROGRESS_COLORS = { 1: '#F99153', 2: '#32C9D3', 3: '#442B8C', 4: '#2D1A6E', 5: '#10B981' };

function BrainWithProgress({ brainLevel, retention, loading, playful }) {
  const getImage = level => {
    const brainImages = {
      1: { src: brain1, size: 42 },
      2: { src: brain2, size: 58 },
      3: { src: brain3, size: 68 },
      4: { src: brain4, size: 74 },
      5: { src: brain5, size: 80 }
    };
    return brainImages[level] || brainImages[1];
  };

  const brainImg = getImage(brainLevel);
  const circumference = 2 * Math.PI * 42; // radius = 42
  const strokeDashoffset = circumference - (retention / 100) * circumference;

  const getProgressColor = level => {
    const colors = playful ? PLAYFUL_PROGRESS_COLORS : CORPORATE_PROGRESS_COLORS;
    return colors[level] || colors[1];
  };

  return (
    <div className="brain-progress">
      <svg
        className="brain-progress__ring"
        viewBox="0 0 100 100"
      >
        {/* Background circle */}
        <circle
          className="brain-progress__ring-bg"
          cx="50"
          cy="50"
          r="42"
          fill="none"
          strokeWidth="6"
        />
        {/* Progress circle */}
        <circle
          className="brain-progress__ring-progress"
          cx="50"
          cy="50"
          r="42"
          fill="none"
          strokeWidth="6"
          strokeLinecap="round"
          style={{
            stroke: getProgressColor(brainLevel),
            strokeDasharray: circumference,
            strokeDashoffset: loading ? circumference : strokeDashoffset,
            transform: 'rotate(-90deg)',
            transformOrigin: '50% 50%',
            transition: 'stroke-dashoffset 0.6s ease-out'
          }}
        />
      </svg>
      <div className="brain-progress__image">
        {!loading && (
          <img
            src={brainImg.src}
            alt="cérebro"
            style={{ width: brainImg.size, height: 'auto' }}
          />
        )}
      </div>
    </div>
  );
}

export function StudyGuideReview() {
  const playful = usePlayfulStudent();
  const [showModal, setShowModal] = useState(false);
  const [showMetricsDialog, setShowMetricsDialog] = useState(false);
  const [brainStatus, setBrainStatus] = useState({
    retentionPercentage: 0,
    cardsDue: 0,
    brainLevel: 1,
    cardsDueTomorrow: 0,
    nextReviewDays: null,
    hasMetrics: false
  });
  const [loading, setLoading] = useState(false);

  const client = useClient();

  const fetchBrainStatus = async () => {
    setLoading(true);
    try {
      const response = await client.fetch('brain/status');
      // brain/status returns plain JSON, not JSON:API format
      // Check both response.data (JSON:API) and response directly (plain JSON)
      const data = response?.data || response;
      if (data && (data.total_cards !== undefined || data.retention_percentage !== undefined)) {
        setBrainStatus({
          retentionPercentage: data.retention_percentage || 0,
          cardsDue: data.cards_due || 0,
          brainLevel: data.brain_level || 1,
          cardsDueTomorrow: data.cards_due_tomorrow || 0,
          nextReviewDays: data.next_review_days,
          hasMetrics: data.has_metrics || false
        });
      }
    } catch (error) {
      console.error('Error fetching brain status:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    // Atualiza brain status após fechar o modal
    setTimeout(() => {
      fetchBrainStatus();
    }, 300);
  };

  useEffect(() => {
    fetchBrainStatus();
  }, []);

  const getStatusDisplay = () => {
    const { cardsDue, retentionPercentage, cardsDueTomorrow } = brainStatus;

    if (cardsDueTomorrow === 0) {
      return {
        title: 'Sem cards',
        subtitle: 'disponíveis'
      };
    }

    if (cardsDue === 0) {
      return {
        title: 'Revisão',
        subtitle: 'em dia'
      };
    }

    if (cardsDue === 1) {
      return {
        title: '1 card',
        subtitle: 'para revisar'
      };
    }

    return {
      title: `${cardsDue} cards`,
      subtitle: 'para revisar'
    };
  };

  const statusDisplay = getStatusDisplay();

  const getBadgeVariant = level => {
    const variants = {
      1: 'danger',
      2: 'third',
      3: 'warning',
      4: 'success-light',
      5: 'success'
    };
    return variants[level] || 'danger';
  };

  const getLevelLabel = level => {
    const labels = {
      1: 'Iniciante',
      2: 'Aprendiz',
      3: 'Praticante',
      4: 'Avançado',
      5: 'Mestre'
    };
    return labels[level] || 'Iniciante';
  };

  const getNextReviewMessage = () => {
    const { nextReviewDays, cardsDue, cardsDueTomorrow } = brainStatus;

    if (cardsDueTomorrow === 0 || cardsDue > 0) return null;

    if (nextReviewDays === null || nextReviewDays === undefined) {
      return {
        title: '🎉 Parabéns!',
        text: 'Sua revisão está em dia. Continue assim!'
      };
    }

    if (nextReviewDays <= 0) {
      return {
        title: '📚 Revisão disponível',
        text: 'Alguns cards já estão prontos para revisar.'
      };
    }

    if (nextReviewDays === 1) {
      return {
        title: '🎉 Tudo certo por hoje!',
        text: 'Volte amanhã para reforçar sua memória. A repetição espaçada ajuda a fixar o conteúdo na memória de longo prazo.'
      };
    }

    return {
      title: '🎉 Excelente trabalho!',
      text: `Próxima revisão em ${nextReviewDays} dias. Quanto mais você acerta, maior o intervalo entre revisões — isso significa que o conteúdo está sendo consolidado!`
    };
  };

  if (playful) {
    return (
      <PlayfulCard className="relative min-w-0 p-5">
        {brainStatus.hasMetrics && (
          <button
            type="button"
            onClick={() => setShowMetricsDialog(true)}
            title="Ver evolução"
            className="absolute right-3 top-3 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white text-[var(--color-secondary)] shadow-card transition-transform active:scale-90"
          >
            <FiBarChart2 className="h-4 w-4" />
          </button>
        )}

        <div className="flex items-center gap-4">
          <BrainWithProgress
            brainLevel={brainStatus.brainLevel}
            retention={brainStatus.retentionPercentage}
            loading={loading}
            playful
          />
          <div className="flex flex-col gap-1.5">
            <Badge
              variant={getBadgeVariant(brainStatus.brainLevel)}
              size="xs"
              className="w-fit rounded-[4px] text-[10px] font-extrabold uppercase leading-none tracking-wide"
            >
              {getLevelLabel(brainStatus.brainLevel)}
            </Badge>
            <div className="flex items-baseline gap-1">
              <span
                className="text-2xl font-extrabold"
                style={{ color: '#222731' }}
              >
                {brainStatus.retentionPercentage.toFixed(0)}%
              </span>
              <span className="text-xs font-semibold text-neutral-500">retenção</span>
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-2xl bg-[var(--color-bg-lilac)] p-3 text-center">
            <span
              className="block text-lg font-extrabold"
              style={{ color: '#222731' }}
            >
              {brainStatus.cardsDue}
            </span>
            <span className="text-[11px] font-semibold text-neutral-500">Para revisar hoje</span>
          </div>
          <div className="rounded-2xl bg-[var(--color-bg-teal)] p-3 text-center">
            <span
              className="block text-lg font-extrabold"
              style={{ color: '#222731' }}
            >
              {brainStatus.cardsDueTomorrow}
            </span>
            <span className="text-[11px] font-semibold text-neutral-500">Para revisar amanhã</span>
          </div>
        </div>

        <PlayfulCardButton
          className="mt-4"
          disabled={brainStatus.cardsDue === 0}
          onClick={() => setShowModal(true)}
        >
          {brainStatus.cardsDue > 0 ? 'Revisar agora' : 'Em dia!'}
        </PlayfulCardButton>

        {getNextReviewMessage() && (
          <div className="mt-4 rounded-2xl bg-[var(--color-bg-cream)] p-3">
            <p
              className="mb-1 text-sm font-extrabold"
              style={{ color: '#222731' }}
            >
              {getNextReviewMessage().title}
            </p>
            <p className="text-xs text-neutral-600">{getNextReviewMessage().text}</p>
          </div>
        )}

        <FlashcardGuideModal
          show={showModal}
          onClose={handleCloseModal}
          update={fetchBrainStatus}
        />

        <RetentionMetricsDialog
          open={showMetricsDialog}
          onClose={() => setShowMetricsDialog(false)}
        />
      </PlayfulCard>
    );
  }

  return (
    <div className="flashcard-widget">
      {brainStatus.hasMetrics && (
        <button
          className="flashcard-widget__metrics-trigger"
          onClick={() => setShowMetricsDialog(true)}
          title="Ver evolução"
          type="button"
        >
          <FiBarChart2 />
        </button>
      )}
      <div className="flashcard-widget__header">
        <BrainWithProgress
          brainLevel={brainStatus.brainLevel}
          retention={brainStatus.retentionPercentage}
          loading={loading}
        />
        <div className="flashcard-widget__info">
          <Badge
            variant={getBadgeVariant(brainStatus.brainLevel)}
            size="xs"
            className="flashcard-widget__badge"
          >
            {getLevelLabel(brainStatus.brainLevel)}
          </Badge>
          <div className="flashcard-widget__retention">
            <span className="flashcard-widget__retention-value">{brainStatus.retentionPercentage.toFixed(0)}%</span>
            <span className="flashcard-widget__retention-label">retenção</span>
          </div>
        </div>
      </div>

      <div className="flashcard-widget__stats">
        <div className="flashcard-widget__stat">
          <span className="flashcard-widget__stat-value">{brainStatus.cardsDue}</span>
          <span className="flashcard-widget__stat-label">Para revisar hoje</span>
        </div>
        <div className="flashcard-widget__stat">
          <span className="flashcard-widget__stat-value">{brainStatus.cardsDueTomorrow}</span>
          <span className="flashcard-widget__stat-label">Para revisar amanhã</span>
        </div>
      </div>

      <Button
        fullWidth
        onClick={() => setShowModal(true)}
        disabled={brainStatus.cardsDue === 0}
      >
        {brainStatus.cardsDue > 0 ? 'Revisar agora' : 'Em dia!'}
      </Button>

      {getNextReviewMessage() && (
        <div className="flashcard-widget__message">
          <p className="flashcard-widget__message-title">{getNextReviewMessage().title}</p>
          <p className="flashcard-widget__message-text">{getNextReviewMessage().text}</p>
        </div>
      )}

      <FlashcardGuideModal
        show={showModal}
        onClose={handleCloseModal}
        update={fetchBrainStatus}
      />

      <RetentionMetricsDialog
        open={showMetricsDialog}
        onClose={() => setShowMetricsDialog(false)}
      />
    </div>
  );
}
